/**
 * CasePaymentsPanel — Phase 4 Payments (G.19).
 *
 * Send a client a Stripe payment link in one press (Lloyd, Oct 2026: "it takes 5 steps to send a
 * payment link. This should be down to 1-2"): the amount is the plan's price unless staff pick a
 * preset or type their own, and Send makes the link and sends it by email and text. Text only goes to
 * a client who agreed to texts, as everywhere. "Create link only" still makes one without sending,
 * for staff who want to copy it. Lists the links already generated for the case with a copy-URL
 * action.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  Link as MuiLink,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import PaymentApi, {
  TicketPaymentDto,
  PaymentsForbiddenError,
  OtrPaymentBlockedError,
  ManualPaymentMethod,
  MANUAL_PAYMENT_METHODS,
  isDeadPayment,
  paymentMethodLabel,
  paymentStatusColor,
  paymentStatusLabel,
  isStripePayment,
  CourtFineDto,
  FeeKind,
  PaymentLinkMessagesDto,
  PaymentLinkOptionsDto,
  paymentLinkSendApi,
} from '../../api/PaymentApi';
import { createPaymentLinkSender } from '../../utils/paymentLinkSend';
import {
  INITIAL_CASE_PRESETS_USD,
  SUBSEQUENT_BILLING_PRESETS_USD,
  formatUsd,
  formatCentsUsd,
} from '../../constants/paymentPresets';
import FeeSuggestions from './FeeSuggestions';

interface CasePaymentsPanelProps {
  Ticket_guid: string;
}

const CasePaymentsPanel: React.FC<CasePaymentsPanelProps> = ({ Ticket_guid }) => {
  const [links, setLinks] = useState<TicketPaymentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [amount, setAmount] = useState<string>(''); // whole dollars
  const [amountTouched, setAmountTouched] = useState(false);
  const [note, setNote] = useState<string>('');

  // Send in one press: where it goes (email on file; text with consent), the plan's price, and the
  // exact words the client gets.
  const [sendOptions, setSendOptions] = useState<PaymentLinkOptionsDto | null>(null);
  const [preview, setPreview] = useState<PaymentLinkMessagesDto | null>(null);
  const [sending, setSending] = useState(false);
  const [sendOutcome, setSendOutcome] = useState<{ sent: boolean; message: string } | null>(null);
  // Retry-safe: a Send whose link was made but did not reach the client sends that same link next.
  const sender = useRef(createPaymentLinkSender(paymentLinkSendApi));
  // Which fee the link is for. Set by "Use" on a fee suggestion and editable beside the amount; a
  // paid appeal fee is what fills PD on the case chart, so it is never guessed from the amount.
  const [feeKind, setFeeKind] = useState<FeeKind | ''>('');

  // OTR cases block a normal payment link (client already paid Off the Record). Once blocked, staff can
  // confirm this is a jury/appeal escalation and retry.
  const [otrBlockMsg, setOtrBlockMsg] = useState<string | null>(null);
  const [isEscalationFee, setIsEscalationFee] = useState(false);

  // The court's fine, and the pass-through it produces (Lloyd, Aug 19 2026). Kept next to the link
  // form because the whole point is that whoever raises the bill can see the split before sending.
  const [courtFine, setCourtFine] = useState<CourtFineDto | null>(null);
  const [courtFineInput, setCourtFineInput] = useState<string>(''); // whole dollars
  const [savingFine, setSavingFine] = useState(false);
  const [fineError, setFineError] = useState<string | null>(null);
  const [isPassThrough, setIsPassThrough] = useState(false);

  // Record a payment the firm already has (cash, Zelle, cheque, or one taken in Clio).
  const [recordOpen, setRecordOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [manualAmount, setManualAmount] = useState<string>(''); // whole dollars
  const [manualMethod, setManualMethod] = useState<ManualPaymentMethod>('cash');
  // Defaults to today; staff can back-date a payment taken earlier (e.g. a Clio backfill).
  const [manualReceivedAt, setManualReceivedAt] = useState<string>(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [manualReference, setManualReference] = useState<string>('');
  const [manualNote, setManualNote] = useState<string>('');
  const [manualFeeKind, setManualFeeKind] = useState<FeeKind | ''>('');

  // G.17/G.18 — permissioned amount on record + pre-paid third-party flag.
  const [canViewAmount, setCanViewAmount] = useState(false);
  const [recordAmount, setRecordAmount] = useState<string>(''); // whole dollars
  const [prepaid, setPrepaid] = useState(false);
  const [savingAmount, setSavingAmount] = useState(false);
  const [feeRefreshKey, setFeeRefreshKey] = useState(0);

  const fetchAmount = useCallback(async () => {
    if (!Ticket_guid) return;
    try {
      const data = await PaymentApi.getPaymentAmount(Ticket_guid);
      setCanViewAmount(true);
      setRecordAmount(
        data.Ticket_PaymentAmountCents != null ? String(data.Ticket_PaymentAmountCents / 100) : '',
      );
      setPrepaid(data.Ticket_PrepaidThirdParty);
    } catch (err) {
      if (err instanceof PaymentsForbiddenError) setCanViewAmount(false);
    }
  }, [Ticket_guid]);

  useEffect(() => {
    fetchAmount();
  }, [fetchAmount]);

  const handleSaveAmount = async () => {
    setSavingAmount(true);
    setError(null);
    try {
      const dollarsVal = recordAmount.trim() === '' ? null : Number(recordAmount);
      if (dollarsVal != null && (!Number.isFinite(dollarsVal) || dollarsVal < 0)) {
        setError('Enter a valid amount, or leave it blank.');
        return;
      }
      const cents = dollarsVal == null ? null : Math.round(dollarsVal * 100);
      await PaymentApi.setPaymentAmount(Ticket_guid, cents, prepaid);
      setSuccess('Payment amount saved.');
      // The saved amount feeds the appeal credit, so the suggestions below are now out of date.
      setFeeRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save amount');
    } finally {
      setSavingAmount(false);
    }
  };

  const fetchLinks = useCallback(async () => {
    if (!Ticket_guid) return;
    setLoading(true);
    setError(null);
    try {
      setLinks(await PaymentApi.listPaymentLinks(Ticket_guid));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payment links');
    } finally {
      setLoading(false);
    }
  }, [Ticket_guid]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const fetchCourtFine = useCallback(async () => {
    try {
      const dto = await PaymentApi.getCourtFine(Ticket_guid);
      setCourtFine(dto);
      setCourtFineInput(
        dto.Ticket_CourtFineCents === null ? '' : (dto.Ticket_CourtFineCents / 100).toFixed(2),
      );
    } catch {
      // Non-fatal: the fine is an aid to billing, not a precondition for it. Leaving the section
      // empty is better than an error banner over a payments panel that otherwise works.
    }
  }, [Ticket_guid]);

  useEffect(() => {
    fetchCourtFine();
  }, [fetchCourtFine]);

  useEffect(() => {
    PaymentApi.getPaymentLinkOptions(Ticket_guid)
      .then((options) => {
        setSendOptions(options);
        // The plan's price, unless staff already picked or typed an amount.
        if (options.planCents && !amountTouched) setAmount(String(options.planCents / 100));
      })
      .catch(() => setSendOptions(null));
    // Loaded once per case; the amount check reads the latest value when it lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Ticket_guid]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(t);
  }, [success]);

  const dollars = Number(amount);
  const amountValid = Number.isFinite(dollars) && dollars >= 0.5 && dollars <= 50000;
  const sendEmail = Boolean(sendOptions && !sendOptions.emailUnavailable);
  const sendSms = Boolean(sendOptions && !sendOptions.smsUnavailable);

  // The words follow the amount, so what staff read is what the client gets.
  const previewCents = Math.round(dollars * 100);
  useEffect(() => {
    if (!amountValid || !sendOptions || sendOptions.mode === 'blocked') {
      setPreview(null);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      PaymentApi.previewPaymentLink(Ticket_guid, previewCents)
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [Ticket_guid, previewCents, amountValid, sendOptions]);

  const resetLinkForm = () => {
    setAmount('');
    setAmountTouched(true);
    setNote('');
    setFeeKind('');
    setOtrBlockMsg(null);
    setIsEscalationFee(false);
    setIsPassThrough(false);
  };

  /** One press: make the link and send it by email and text (text only with consent). */
  const handleSend = async () => {
    if (!amountValid) {
      setError('Enter an amount between $0.50 and $50,000.');
      return;
    }
    if (!sendEmail && !sendSms) {
      setError('This client has no email on file and cannot be texted. Create the link only, and send it another way.');
      return;
    }
    setSending(true);
    setError(null);
    setSendOutcome(null);
    try {
      const base = courtFine?.Ticket_CourtFineCents ?? null;
      const result = await sender.current.send({
        ticketGuid: Ticket_guid,
        amountCents: Math.round(dollars * 100),
        note,
        channels: { email: sendEmail, sms: sendSms },
        create: {
          isEscalationFee,
          passThrough: isPassThrough && base ? { passThroughBaseCents: base } : undefined,
          feeKind: isPassThrough ? null : feeKind || null,
        },
      });
      const message = `Payment link for ${formatUsd(dollars)}: ${result.reason}`;
      setSendOutcome({ sent: result.sent, message });
      if (result.sent) {
        resetLinkForm();
      } else {
        // Made but not received: the next Send sends this same link, not a second one.
        sender.current = createPaymentLinkSender(paymentLinkSendApi, sender.current.lastLink());
      }
      await fetchLinks();
    } catch (err) {
      if (err instanceof OtrPaymentBlockedError) {
        setOtrBlockMsg(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'The payment link could not be sent.');
        await fetchLinks();
      }
    } finally {
      setSending(false);
    }
  };

  const handleCreate = async () => {
    if (!amountValid) {
      setError('Enter an amount between $0.50 and $50,000.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const amountCents = Math.round(dollars * 100);
      const base = courtFine?.Ticket_CourtFineCents ?? null;
      await PaymentApi.createPaymentLink(
        Ticket_guid,
        amountCents,
        note,
        isEscalationFee,
        isPassThrough && base ? { passThroughBaseCents: base } : undefined,
        feeKind || null,
      );
      setSuccess(`Payment link created for ${formatUsd(dollars)}. It has not been sent.`);
      resetLinkForm();
      await fetchLinks();
    } catch (err) {
      if (err instanceof OtrPaymentBlockedError) {
        // Reveal the escalation confirmation rather than a dead-end error.
        setOtrBlockMsg(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create payment link');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleSaveCourtFine = async () => {
    setSavingFine(true);
    setFineError(null);
    try {
      const trimmed = courtFineInput.trim();
      const value = trimmed === '' ? null : Math.round(Number(trimmed) * 100);
      if (value !== null && (!Number.isFinite(value) || value < 0)) {
        setFineError('Enter the fine as the court states it, or leave it blank.');
        return;
      }
      setCourtFine(await PaymentApi.setCourtFine(Ticket_guid, value));
    } catch (err) {
      setFineError(err instanceof Error ? err.message : 'Failed to save the court fine');
    } finally {
      setSavingFine(false);
    }
  };

  const manualDollars = Number(manualAmount);
  const manualAmountValid =
    Number.isFinite(manualDollars) && manualDollars > 0 && manualDollars <= 50000;

  const handleRecordPayment = async () => {
    if (!manualAmountValid) {
      setError('Enter an amount between $0.01 and $50,000.');
      return;
    }
    setRecording(true);
    setError(null);
    try {
      await PaymentApi.recordManualPayment(Ticket_guid, {
        amountCents: Math.round(manualDollars * 100),
        method: manualMethod,
        // Send a full timestamp so the server stores a real instant, not a midnight-UTC edge case.
        receivedAt: manualReceivedAt
          ? new Date(`${manualReceivedAt}T12:00:00`).toISOString()
          : undefined,
        reference: manualReference,
        note: manualNote,
        feeKind: manualFeeKind || null,
      });
      setSuccess(
        `Recorded ${formatUsd(manualDollars)} paid by ${paymentMethodLabel(manualMethod)}.`,
      );
      setManualAmount('');
      setManualReference('');
      setManualNote('');
      setManualFeeKind('');
      setRecordOpen(false);
      // The case total feeds the appeal credit, so the suggestions above are now stale.
      setFeeRefreshKey((k) => k + 1);
      await fetchLinks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record payment');
    } finally {
      setRecording(false);
    }
  };

  const handleVoid = async (payment: TicketPaymentDto) => {
    const reason = window.prompt(
      `Void this ${formatCentsUsd(payment.TicketPayment_AmountCents)} payment? It stays on the record for audit but stops counting toward the case total.\n\nReason (optional):`,
    );
    // prompt returns null when cancelled; an empty string is a deliberate "no reason given".
    if (reason === null) return;
    setError(null);
    try {
      await PaymentApi.voidPayment(Ticket_guid, payment.TicketPayment_guid, reason);
      setSuccess('Payment voided.');
      setFeeRefreshKey((k) => k + 1);
      await fetchLinks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to void payment');
    }
  };

  const handleCancelLink = async (payment: TicketPaymentDto) => {
    const reason = window.prompt(
      `Cancel this ${formatCentsUsd(payment.TicketPayment_AmountCents)} payment link? It stops working straight away, so a client who still has it cannot pay the wrong amount. The record is kept.\n\nReason (optional):`,
    );
    if (reason === null) return;
    setError(null);
    try {
      await PaymentApi.cancelPaymentLink(Ticket_guid, payment.TicketPayment_guid, reason);
      setSuccess('Payment link cancelled. It can no longer be paid.');
      setFeeRefreshKey((k) => k + 1);
      await fetchLinks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel payment link');
    }
  };

  const handleCopy = async (url: string | null) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setSuccess('Link copied to clipboard.');
    } catch {
      setError('Could not copy the link.');
    }
  };

  /** "What is this for" — the same three fees the suggestions offer, or nothing said. */
  const renderFeeKindSelect = (
    id: string,
    value: FeeKind | '',
    onChange: (next: FeeKind | '') => void,
    disabled = false,
  ) => (
    <FormControl size="small" sx={{ minWidth: 200 }} disabled={disabled}>
      <InputLabel id={id}>What is it for</InputLabel>
      <Select
        labelId={id}
        label="What is it for"
        value={value}
        onChange={(e) => onChange(e.target.value as FeeKind | '')}
      >
        <MenuItem value="">
          <em>Not said</em>
        </MenuItem>
        <MenuItem value="flat">Pre-litigation flat fee</MenuItem>
        <MenuItem value="appeal">Appeal fee</MenuItem>
        <MenuItem value="jury">Jury trial fee</MenuItem>
      </Select>
    </FormControl>
  );

  const renderPresetRow = (label: string, presets: number[]) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {presets.map((p) => (
          <Chip
            key={`${label}-${p}`}
            label={formatUsd(p)}
            size="small"
            variant={Number(amount) === p ? 'filled' : 'outlined'}
            color={Number(amount) === p ? 'primary' : 'default'}
            onClick={() => {
              setAmountTouched(true);
              setAmount(String(p));
            }}
          />
        ))}
      </Box>
    </Box>
  );

  return (
    <Box>
      {canViewAmount && (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Amount on record
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1, alignItems: 'center' }}>
            <TextField
              label="Case amount (USD)"
              type="number"
              size="small"
              value={recordAmount}
              onChange={(e) => setRecordAmount(e.target.value)}
              inputProps={{ min: 0, step: 1 }}
              sx={{ maxWidth: 200 }}
            />
            <FormControlLabel
              control={<Checkbox checked={prepaid} onChange={(e) => setPrepaid(e.target.checked)} />}
              label="Pre-paid by third party (no charge)"
            />
            <Button variant="outlined" onClick={handleSaveAmount} disabled={savingAmount}>
              {savingAmount ? 'Saving…' : 'Save amount'}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Visible only to staff with payment access. Pre-paid third-party cases (CDL Consultants /
            CDL Legal / OTR) record the amount without charging.
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      <FeeSuggestions
        Ticket_guid={Ticket_guid}
        onUseAmount={(dollars, kind) => {
          setAmount(dollars);
          setFeeKind(kind);
          setIsPassThrough(false);
        }}
        refreshKey={feeRefreshKey}
      />

      {/*
        The court's fine, and what it means for a bill. This sits ABOVE the link form on purpose:
        the pass-through amount is derived from it, and the failure this replaces was three
        different figures being argued out by hand in an email thread (Frederick Person, Aug 2026).
      */}
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
        The court's fine
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
        <TextField
          label="Court fine (USD)"
          type="number"
          size="small"
          value={courtFineInput}
          onChange={(e) => setCourtFineInput(e.target.value)}
          inputProps={{ min: 0, step: 0.01 }}
          helperText="What the COURT charges — not what the client pays us"
          sx={{ maxWidth: 240 }}
        />
        <Button
          variant="outlined"
          size="small"
          sx={{ mt: 0.5 }}
          onClick={handleSaveCourtFine}
          disabled={savingFine}
        >
          {savingFine ? 'Saving…' : 'Save fine'}
        </Button>
      </Stack>
      {fineError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {fineError}
        </Alert>
      )}
      {courtFine?.passThroughQuote && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Collecting this fine for the court?</strong> Bill{' '}
          <strong>{formatCentsUsd(courtFine.passThroughQuote.totalCents)}</strong> —{' '}
          {formatCentsUsd(courtFine.passThroughQuote.courtFineCents)} to the court plus{' '}
          {formatCentsUsd(courtFine.passThroughQuote.addOnCents)} for the firm (
          {courtFine.passThroughQuote.percent}% for the card fee, the money order and the trip to
          the Post Office).
          <Button
            size="small"
            sx={{ ml: 1 }}
            onClick={() => {
              setAmount((courtFine.passThroughQuote!.totalCents / 100).toFixed(2));
              setIsPassThrough(true);
              setFeeKind('');
            }}
          >
            Use this amount
          </Button>
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
        Send a payment link
      </Typography>

      {renderPresetRow('Initial case', INITIAL_CASE_PRESETS_USD)}
      {renderPresetRow('Subsequent billing', SUBSEQUENT_BILLING_PRESETS_USD)}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1, mb: 1 }}>
        <TextField
          label="Amount (USD)"
          type="number"
          size="small"
          value={amount}
          onChange={(e) => {
            setAmountTouched(true);
            setAmount(e.target.value);
          }}
          inputProps={{ min: 0.5, max: 50000, step: 1 }}
          helperText={
            sendOptions?.planCents && !amountTouched ? `The price of ${sendOptions.plan}` : undefined
          }
          sx={{ maxWidth: 200 }}
        />
        {/* A pass-through is the court's money, never one of the firm's fees. */}
        {renderFeeKindSelect('link-fee-kind-label', isPassThrough ? '' : feeKind, setFeeKind, isPassThrough)}
        <TextField
          label="Note (optional — internal)"
          size="small"
          fullWidth
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Reason for a custom charge (kept internal)"
        />
      </Stack>

      {courtFine?.Ticket_CourtFineCents ? (
        <FormControlLabel
          control={
            <Checkbox
              checked={isPassThrough}
              onChange={(e) => setIsPassThrough(e.target.checked)}
            />
          }
          label={`This is the court's money passing through us — keep ${formatCentsUsd(
            courtFine.Ticket_CourtFineCents,
          )} out of firm revenue`}
        />
      ) : null}

      {otrBlockMsg && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {otrBlockMsg}
          <FormControlLabel
            sx={{ display: 'block', mt: 1 }}
            control={
              <Checkbox
                checked={isEscalationFee}
                onChange={(e) => setIsEscalationFee(e.target.checked)}
              />
            }
            label="This is a jury trial / county appeal fee — bill the client directly"
          />
        </Alert>
      )}

      {sendOptions && sendOptions.mode !== 'blocked' && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {sendEmail ? `Email to ${sendOptions.emailAddress}.` : `No email: ${sendOptions.emailUnavailable}.`}{' '}
          {sendSms ? `Text to ${sendOptions.phoneNumber}.` : `No text: ${sendOptions.smsUnavailable}.`}
        </Typography>
      )}
      {sendOptions?.mode === 'partner' && sendOptions.note && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {sendOptions.note}
        </Alert>
      )}
      {preview && (sendEmail || sendSms) && (
        <Box component="details" open sx={{ mb: 1 }}>
          <Box component="summary" sx={{ cursor: 'pointer' }}>
            <Typography variant="body2" component="span">What the client will receive</Typography>
          </Box>
          <Card variant="outlined" sx={{ mt: 1 }}>
            <CardContent>
              {sendEmail && (
                <>
                  <Typography variant="body2">
                    <strong>Email:</strong> {preview.email.subject}
                  </Typography>
                  <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 14, m: 0 }}>
                    {preview.email.body}
                  </Box>
                </>
              )}
              {sendSms && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Text:</strong> {preview.sms}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {sendOutcome && (
        <Alert severity={sendOutcome.sent ? 'success' : 'warning'} sx={{ mb: 1 }} onClose={() => setSendOutcome(null)}>
          {sendOutcome.message}
        </Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: otrBlockMsg ? 1 : 0 }}>
        <Button
          variant="contained"
          onClick={() => void handleSend()}
          disabled={
            sending || creating || !amountValid || (!sendEmail && !sendSms) || (otrBlockMsg !== null && !isEscalationFee)
          }
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {sending ? 'Sending…' : otrBlockMsg ? 'Send escalation payment link' : 'Send payment link'}
        </Button>
        <Button
          variant="text"
          onClick={handleCreate}
          disabled={sending || creating || !amountValid || (otrBlockMsg !== null && !isEscalationFee)}
          startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {creating ? 'Creating…' : "Create link only (don't send)"}
        </Button>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/*
        Record money the firm already has. Separate from the link flow above on purpose: that one
        asks a client to pay, this one states that they already did (Lloyd, Jul 29 2026).
      */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Payment received another way
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={() => setRecordOpen((open) => !open)}>
          {recordOpen ? 'Cancel' : 'Record a payment'}
        </Button>
      </Box>

      {recordOpen && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            For money taken outside this site — cash, Zelle, a check, or a payment already made in
            Clio. It counts toward the case total straight away.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Amount (USD)"
              type="number"
              size="small"
              value={manualAmount}
              onChange={(e) => setManualAmount(e.target.value)}
              inputProps={{ min: 0.01, max: 50000, step: 1 }}
              sx={{ maxWidth: 180 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="manual-method-label">Paid by</InputLabel>
              <Select
                labelId="manual-method-label"
                label="Paid by"
                value={manualMethod}
                onChange={(e) => setManualMethod(e.target.value as ManualPaymentMethod)}
              >
                {MANUAL_PAYMENT_METHODS.map((method) => (
                  <MenuItem key={method} value={method}>
                    {paymentMethodLabel(method)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Date received"
              type="date"
              size="small"
              value={manualReceivedAt}
              onChange={(e) => setManualReceivedAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().slice(0, 10) }}
              sx={{ maxWidth: 190 }}
            />
            {renderFeeKindSelect('manual-fee-kind-label', manualFeeKind, setManualFeeKind)}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Reference (optional)"
              size="small"
              value={manualReference}
              onChange={(e) => setManualReference(e.target.value)}
              placeholder="Check no. / Zelle confirmation / Clio id"
              sx={{ minWidth: 260 }}
            />
            <TextField
              label="Note (optional — internal)"
              size="small"
              fullWidth
              value={manualNote}
              onChange={(e) => setManualNote(e.target.value)}
            />
          </Stack>

          <Button
            variant="contained"
            onClick={handleRecordPayment}
            disabled={recording || !manualAmountValid}
            startIcon={recording ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {recording ? 'Recording…' : 'Record payment'}
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
        Payments ({links.length})
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : links.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>
          No payments or payment links on this case yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {links.map((link) => {
            const paid = link.TicketPayment_Status === 'paid';
            const dead = isDeadPayment(link.TicketPayment_Status);
            // Only a hand-entered payment can be voided here — a Stripe row is corrected by
            // refunding in Stripe, so we do not offer an action that would desync the two.
            const canVoid = !dead && !isStripePayment(link.TicketPayment_Method);
            // A link nobody has paid can be cancelled, which is the opposite case to voiding: there
            // is no money to refund, and the point is to stop some arriving on a replaced link.
            const canCancelLink = !paid && !dead && Boolean(link.TicketPayment_Url);

            return (
              <Card
                key={link.TicketPayment_guid}
                variant="outlined"
                sx={dead ? { opacity: 0.6 } : undefined}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        textDecoration: dead ? 'line-through' : 'none',
                      }}
                    >
                      {formatCentsUsd(link.TicketPayment_AmountCents)}
                    </Typography>
                    <Chip
                      label={paymentStatusLabel(link.TicketPayment_Status)}
                      size="small"
                      variant="outlined"
                      color={paymentStatusColor(link.TicketPayment_Status)}
                    />
                    <Chip
                      label={paymentMethodLabel(link.TicketPayment_Method)}
                      size="small"
                      variant="outlined"
                    />
                    <Box sx={{ flex: 1 }} />
                    {link.TicketPayment_Url && (
                      <>
                        <Tooltip title="Copy link">
                          <IconButton size="small" onClick={() => handleCopy(link.TicketPayment_Url)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Open link">
                          <IconButton
                            size="small"
                            component={MuiLink}
                            href={link.TicketPayment_Url}
                            target="_blank"
                            rel="noopener"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {canCancelLink && (
                      <Button size="small" color="error" onClick={() => handleCancelLink(link)}>
                        Cancel link
                      </Button>
                    )}
                    {canVoid && (
                      <Button size="small" color="error" onClick={() => handleVoid(link)}>
                        Void
                      </Button>
                    )}
                  </Box>
                  {link.TicketPayment_Reference && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Ref: {link.TicketPayment_Reference}
                    </Typography>
                  )}
                  {link.TicketPayment_Note && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-line' }}
                    >
                      {link.TicketPayment_Note}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                    {link.TicketPayment_CreatedByName || 'Staff'} ·{' '}
                    {link.TicketPayment_ReceivedAt
                      ? `received ${new Date(link.TicketPayment_ReceivedAt).toLocaleDateString('en-US')}`
                      : `created ${new Date(link.TicketPayment_CreatedAt).toLocaleString('en-US')}`}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default CasePaymentsPanel;
